/**
 * Admin / Credentials — read-only status checklist.
 *
 * Lists every env-var the backend reads, grouped by category, with set /
 * missing badges. Values are NEVER shown — only `set: bool` + the byte
 * length so an operator can spot a truncated paste.
 *
 * Editing happens by SSHing to the host, editing
 *   /root/ctodashboard/backend/.env
 * and restarting cto-api / cto-worker / cto-beat. We deliberately don't
 * expose a credential editor UI — too easy to leak through audit logs,
 * accidental screen-shares, or auth-flow regressions.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Key, RefreshCw, ShieldCheck, ShieldAlert, ShieldX, Search,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Cred {
  key: string
  description: string
  required: boolean
  set: boolean
  value_length: number
}
interface Category { name: string; entries?: Cred[]; items?: Cred[] }
interface Status {
  categories: Category[]
  counts?: { set: number; missing: number; required_missing: number; total: number }
  total?: number
  set?: number
  missing_required?: number
  note?: string
  generated_at?: string
}

export default function CredentialsPage() {
  const [data, setData] = useState<Status | null>(null)
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState('')
  const [hideSet, setHideSet] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/admin/credentials/status')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const counts = useMemo(() => {
    if (!data) return { set: 0, total: 0, required_missing: 0 }
    if (data.counts) return data.counts
    return {
      set:              data.set ?? 0,
      total:            data.total ?? 0,
      required_missing: data.missing_required ?? 0,
    }
  }, [data])

  // Some endpoints return entries, some items — normalise.
  const cats: { name: string; rows: Cred[] }[] = useMemo(() => {
    if (!data) return []
    return data.categories.map(c => ({
      name: c.name,
      rows: (c.entries || c.items || []).filter(r => {
        if (hideSet && r.set) return false
        if (!filter) return true
        const q = filter.toLowerCase()
        return r.key.toLowerCase().includes(q) || r.description.toLowerCase().includes(q)
      }),
    })).filter(c => c.rows.length > 0)
  }, [data, filter, hideSet])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Key size={18} className="text-amber-light" /> CREDENTIALS · STATUS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          read-only · values never leave the server
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3">
        <Tile label="Set"               value={counts.set}              total={counts.total} tone="emerald" />
        <Tile label="Required missing"  value={counts.required_missing} tone={counts.required_missing > 0 ? 'rose' : 'muted'} />
        <Tile label="Total tracked"     value={counts.total}            tone="muted" />
      </div>

      {/* Editing instructions */}
      <div className="card p-3 border-border bg-surface-raised/40">
        <div className="text-[11px] uppercase tracking-widest text-text-muted font-semibold mb-1.5">
          How to set / update
        </div>
        <ol className="list-decimal list-inside text-xs text-text-secondary space-y-1">
          <li>SSH to the host and edit <code className="font-mono text-text-primary">/root/ctodashboard/backend/.env</code></li>
          <li>Add / update the key in <code className="font-mono">KEY=value</code> form (no quotes needed unless the value contains spaces)</li>
          <li>Restart the affected services: <code className="font-mono">docker restart cto-api cto-worker cto-beat</code></li>
          <li>Refresh this page — the row should flip to ✓ Set</li>
        </ol>
        <div className="mt-2 text-[10px] text-text-muted">
          The .env file is excluded from version control. The source code never contains credentials.
          {data?.note && <> · {data.note}</>}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="input pl-7 w-[260px]" placeholder="Filter by key or description"
            value={filter} onChange={e => setFilter(e.target.value)}
            aria-label="Filter credentials" />
        </div>
        <label className="text-[11px] flex items-center gap-1.5 text-text-muted cursor-pointer">
          <input type="checkbox" checked={hideSet} onChange={e => setHideSet(e.target.checked)} />
          Hide already-set
        </label>
      </div>

      {/* Categories */}
      {cats.map(c => (
        <section key={c.name} className="card p-0 overflow-hidden">
          <header className="p-3 border-b border-border flex items-center gap-2">
            <h2 className="text-sm font-semibold text-text-primary">{c.name}</h2>
            <span className="text-[10px] text-text-muted">
              {c.rows.filter(r => r.set).length} / {c.rows.length} set
            </span>
          </header>
          <table className="w-full text-xs">
            <thead className="bg-surface-raised text-text-muted">
              <tr>
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2 font-mono w-1/4">Key</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2 w-16">Length</th>
                <th className="text-right px-3 py-2 w-24">Required</th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map(r => (
                <tr key={r.key} className="border-t border-border">
                  <td className="px-3 py-1.5">
                    {r.set
                      ? <ShieldCheck size={14} className="text-emerald" aria-label="Set" />
                      : r.required
                        ? <ShieldX size={14} className="text-rose" aria-label="Required, missing" />
                        : <ShieldAlert size={14} className="text-text-muted" aria-label="Optional, missing" />}
                  </td>
                  <td className="px-3 py-1.5 font-mono">
                    <span className={clsx('font-semibold', r.set ? 'text-text-primary' : r.required ? 'text-rose-light' : 'text-text-muted')}>
                      {r.key}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary">{r.description}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-text-muted">
                    {r.set ? r.value_length + ' B' : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {r.required ? (
                      <span className="badge bg-rose/10 text-rose-light border-rose/30 border">required</span>
                    ) : (
                      <span className="badge bg-text-muted/10 text-text-muted border-text-muted/20 border">optional</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {cats.length === 0 && data && (
        <div className="card p-8 text-text-muted italic text-center">
          No credentials match the filter.
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, total, tone }: {
  label: string; value: number; total?: number; tone: 'emerald' | 'rose' | 'muted'
}) {
  const c = tone === 'emerald' ? 'text-emerald' : tone === 'rose' ? 'text-rose' : 'text-text-muted'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>
        {value.toLocaleString()}
        {total !== undefined && (
          <span className="text-text-muted text-base"> / {total.toLocaleString()}</span>
        )}
      </div>
    </div>
  )
}
